import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Flex, Text, useStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {useProduct, useProducts} from '@salesforce/commerce-sdk-react'
import ProductCompareTile from './partials/product-compare-tile'
import ProductCompareGrid from './partials/product-compare-grid'
import Dropdown from '../dropdown'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import useSpecifications from '../../utils/specifications-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const ProductCompare = ({product}) => {
    const [selectedValue, setSelectedValue] = useState(null)
    const [isGridExpanded, setIsGridExpanded] = useState(false)
    const styles = useStyleConfig('ProductCompare')
    const comparableProductsIds = (product?.c_recommendations || [])
        .filter((rec) => rec?.recommendationType?.value === 3)
        .map((rec) => rec.recommendedItemId)

    const {currency: activeCurrency} = useCurrency()

    const updatedProduct = {
        ...product,
        specifications: useSpecifications(product),
    }

    const {data: comparedProduct} = useProduct(
        {
            parameters: {
                id: selectedValue,
                currency: activeCurrency,
                c_includeTechSpecs: true,
            },
        },
        {keepPreviousData: true},
    )

    const comparedProductSpec = useSpecifications(comparedProduct)
    const updatedComparedProduct = comparedProduct && {
        ...comparedProduct,
        specifications: comparedProductSpec,
    }

    const {data: comparableProductsObjs} = useProducts(
        {
            parameters: {
                ids: comparableProductsIds,
                expand: ['availability', 'images'],
                c_includeTechSpecs: true,
            },
            headers: {
                'x-debug': 'product-compare-index',
            },
        },
        {
            select: (result) =>
                result?.data?.map((item) => ({
                    label: item.name,
                    id: item.id,
                })),
            enabled: comparableProductsIds.length > 0,
        },
    )

    useEffect(() => {
        if (!selectedValue && comparableProductsObjs?.length > 0) {
            setSelectedValue(comparableProductsObjs[0].id)
        }
    }, [comparableProductsObjs, selectedValue])

    const mergedSpecs = []

    const productValueNames = (updatedProduct?.specifications || []).flatMap((item) =>
        item.values.map((v) => v.value_name),
    )
    const comparedValueNames = (updatedComparedProduct?.specifications || []).flatMap((item) =>
        item.values.map((v) => v.value_name),
    )

    const allValueNames = [...new Set([...productValueNames, ...comparedValueNames])]

    allValueNames.forEach((valueName) => {
        const productValueObj = (updatedProduct?.specifications || [])
            .flatMap((item) => item.values)
            .find((v) => v.value_name === valueName)

        const comparedValueObj = (updatedComparedProduct?.specifications || [])
            .flatMap((item) => item.values)
            .find((v) => v.value_name === valueName)

        const productValue = productValueObj?.value
            ? String(productValueObj.value).split(',').join(', ')
            : '-'

        const comparedValue = comparedValueObj?.value
            ? String(comparedValueObj.value).split(',').join(', ')
            : '-'

        mergedSpecs.push({
            id: valueName,
            label: valueName,
            values: [productValue, comparedValue],
        })
    })

    const gridHeadings = mergedSpecs.map((spec) => ({
        key: spec.id,
        label: spec.label,
        values: spec.values,
    }))

    const gridHeadingsToDisplay = isGridExpanded ? gridHeadings : gridHeadings.slice(0, 6)

    const dataToDisplay = gridHeadingsToDisplay.map((heading) => {
        return {
            id: heading.key,
            label: heading?.label,
            values: heading?.values,
        }
    })

    const handleSelectChange = async (selectedOption) => {
        const optionId = selectedOption && selectedOption.id
        setSelectedValue(optionId)
    }

    const handleGridToggle = () => {
        setIsGridExpanded(!isGridExpanded)
    }

    if (!updatedComparedProduct) return

    return (
        <Flex data-testid="product-compare-section" {...styles.productCompareContainer}>
            <Flex {...styles.headerContainer}>
                <Text {...styles.headerText}>
                    <FormattedMessage
                        defaultMessage="Compare products"
                        id="product-compare.header"
                    />
                </Text>
            </Flex>
            {/* Placeholder Select component */}
            <Flex {...styles.selectContainer}>
                {isHydrated() && (
                    <Dropdown
                        data-testid="product-compare-select"
                        onChange={handleSelectChange}
                        value={
                            comparableProductsObjs?.find((item) => item.id === selectedValue) ||
                            null
                        }
                        placeholder={!selectedValue ? 'Select a product' : ''}
                        options={comparableProductsObjs || []}
                        variant="pdpDropdown"
                        name=""
                        label=""
                    />
                )}
            </Flex>
            <Flex {...styles.productSpecsContainer}>
                {/* Empty div to maintain distance between elements for grid */}
                <Flex w={{base: 0, lg: '33.33%'}} display={{base: 'none', lg: 'flex'}} />
                {updatedProduct && <ProductCompareTile product={updatedProduct} />}
                <ProductCompareTile
                    product={selectedValue ? updatedComparedProduct : updatedProduct}
                />
            </Flex>
            <ProductCompareGrid gridData={dataToDisplay} isOnPDP />
            <Flex {...styles.specsButtonContainer}>
                <Button
                    data-testid="specifications-button"
                    variant="text-button"
                    onClick={handleGridToggle}
                    {...styles.specsButton}
                >
                    <Text {...styles?.specsButtonText}>
                        {isGridExpanded ? (
                            <FormattedMessage
                                defaultMessage="Close specs"
                                id="product_compare.row.button.close_specs"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Open specs"
                                id="product_compare.row.button.open_specs"
                            />
                        )}
                    </Text>
                </Button>
            </Flex>
        </Flex>
    )
}

ProductCompare.propTypes = {product: PropTypes.object}
export default ProductCompare
